import { fetchAirMeshToken, fetchAirMeshChannels } from 'apis/airspace.api';
import {
	setAMConnectionLoading,
	setAMConnectionError,
	setAMWebSocketConnected,
	resetAMState,
	airMeshChannelsSelector,
	setReconnectAttempts,
	setNextRetryTime
} from 'reducers/liveTrafficSlice';
import { processData } from 'utils/airMeshHelpers';
import { fetchEventSource } from '@microsoft/fetch-event-source';

let abortController = null;
let tokenRefreshTimeout = null;
let pollingInterval = null;
let isIntentionalAbort = false;
let reconnectAttempts = 0;
const RETRY_INTERVAL = 60000; // 1 minute

let hasScheduledRetry = false;
let isHandlingError = false; // NEW: guard for error handling
let isRetrying = false;
let isFirstChannelPoll = true; // NEW: flag to skip first channel difference check

const getJWTExpiration = token => {
	const payload = JSON.parse(atob(token.split('.')[1]));
	return payload.exp * 1000;
};

const scheduleTokenRefresh = expiresAt => (dispatch, getState) => {
	const timeUntilExpiry = expiresAt - Date.now();
	const refreshBuffer = 2 * 60 * 1000; // 2 minutes
	const refreshTime = Math.max(0, timeUntilExpiry - refreshBuffer);
	if (tokenRefreshTimeout) {
		clearTimeout(tokenRefreshTimeout);
	}

	const refresh = async (isRetry = false) => {
		try {
			await dispatch(subscribeToSSE(false));
		} catch (error) {
			console.error('[SSE] Token refresh failed:', error);
			if (!isRetry) {
				setTimeout(() => refresh(true), 5000);
			}
		}
	};

	if (refreshTime <= 0) {
		refresh();
		return;
	}
	tokenRefreshTimeout = setTimeout(refresh, refreshTime);
};

export const pollAirmeshChannels = () => async (dispatch, getState) => {
	if (pollingInterval) {
		clearInterval(pollingInterval);
	}
	const pollChannels = async () => {
		const previousChannels = airMeshChannelsSelector(getState());
		await dispatch(fetchAirMeshChannels());
		const latestChannels = airMeshChannelsSelector(getState());

		// Skip the first channel difference check
		if (isFirstChannelPoll) {
			isFirstChannelPoll = false;
			return;
		}

		if (JSON.stringify(latestChannels) !== JSON.stringify(previousChannels)) {
			await dispatch(subscribeToSSE(false));
		}
	};
	pollingInterval = setInterval(pollChannels, 60000);
	await pollChannels();
};

let sseBuffer = [];
let flushTimeout = null;

const clearSseBuffer = () => {
	sseBuffer = [];
	if (flushTimeout) {
		clearTimeout(flushTimeout);
		flushTimeout = null;
	}
};

// Helper to remove integer suffix from provider string (e.g., 'airmesh_sdk_12' -> 'airmesh_sdk')
const removeIntFromProvider = string => {
	if (!string) return '';
	return string.replace(/_\d+/g, '');
};

// Check if item should get real-time updates (all airmesh_sdk traffic)
function hasRealTimeData(item) {
	if (!item) return false;
	const provider = removeIntFromProvider(item.provider);
	return provider === 'airmesh_sdk' || provider === 'aloft_simulator';
}

function bufferAndDispatch(dispatch, incomingItem) {
	// Separate items based on whether they need real-time updates
	const realTimeItems = [];
	const batchedItems = [];

	incomingItem.forEach(item => {
		if (hasRealTimeData(item)) {
			realTimeItems.push(item);
		} else {
			batchedItems.push(item);
		}
	});

	// Dispatch real-time items immediately (all airmesh_sdk traffic)
	if (realTimeItems.length > 0) {
		dispatch(processData(realTimeItems));
	}

	// Buffer and batch regular traffic (ADS-B, ASTERIX, other providers, etc.)
	if (batchedItems.length > 0) {
		sseBuffer.push(batchedItems);
		if (!flushTimeout) {
			flushTimeout = setTimeout(() => {
				dispatch(processData(sseBuffer.flat()));
				sseBuffer = [];
				flushTimeout = null;
			}, 500); //Update data every 500ms
		}
	}
}

// Internal retry function
const retryConnection = (dispatch, isManualRetry = false) => {
	if (hasScheduledRetry && !isManualRetry) {
		return;
	}
	hasScheduledRetry = true;
	isRetrying = true;

	if (window.retryTimeout) {
		clearTimeout(window.retryTimeout);
		window.retryTimeout = null;
	}

	reconnectAttempts += 1;
	dispatch(setReconnectAttempts(reconnectAttempts));

	const delay = reconnectAttempts === 1 ? 0 : RETRY_INTERVAL;

	// Set the next retry time for the progress indicator
	const nextRetryTime = Date.now() + delay;
	dispatch(setNextRetryTime(nextRetryTime));

	// Only set error state if we're past the first retry
	if (reconnectAttempts > 1) {
		dispatch(setAMConnectionError(true));
	}

	window.retryTimeout = setTimeout(() => {
		window.retryTimeout = null;
		hasScheduledRetry = false;
		isHandlingError = false;
		isRetrying = false;
		dispatch(subscribeToSSE(false));
	}, delay);
};

// Export manual retry action creator
export const manualRetry = () => async dispatch => {
	if (hasScheduledRetry) {
		if (window.retryTimeout) {
			clearTimeout(window.retryTimeout);
			window.retryTimeout = null;
		}
		hasScheduledRetry = false;
	}

	hasScheduledRetry = true;
	isRetrying = true;

	reconnectAttempts += 1;
	dispatch(setReconnectAttempts(reconnectAttempts));

	// Set the next retry time for the progress indicator
	const nextRetryTime = Date.now();
	dispatch(setNextRetryTime(nextRetryTime));

	// Only set error state if we're past the first retry
	if (reconnectAttempts > 1) {
		dispatch(setAMConnectionError(true));
	}

	// Execute retry immediately
	hasScheduledRetry = false;
	isHandlingError = false;
	isRetrying = false;
	await dispatch(subscribeToSSE(false));
};

export const subscribeToSSE =
	(showLoader = true) =>
	async (dispatch, getState) => {
		isIntentionalAbort = false;
		isHandlingError = false;
		clearSseBuffer();

		// Only show loader and reset attempts on first connection
		if (showLoader && reconnectAttempts === 0) {
			dispatch(setAMConnectionLoading(true));
		}

		// Only reset state on first connection
		if (showLoader && reconnectAttempts === 0) {
			if (window.retryTimeout) {
				clearTimeout(window.retryTimeout);
				window.retryTimeout = null;
			}
			hasScheduledRetry = false;
			reconnectAttempts = 0;
			dispatch(setReconnectAttempts(0));
			dispatch(setNextRetryTime(null));
		}

		if (abortController) {
			abortController.abort();
		}

		abortController = new AbortController();

		try {
			const { token, url } = await dispatch(fetchAirMeshToken());
			const expiresAt = getJWTExpiration(token);
			dispatch(scheduleTokenRefresh(expiresAt));

			const urlToUse = new URL(url);
			urlToUse.searchParams.append('topic', '*');

			try {
				await fetchEventSource(urlToUse.toString(), {
					method: 'GET',
					withCredentials: true,
					headers: {
						Authorization: `Bearer ${token}`,
						Accept: 'text/event-stream'
					},
					signal: abortController.signal,
					openWhenHidden: true,
					retry: false,

					onopen: async response => {
						if (!response.ok) {
							throw new Error(`Server returned ${response.status}`);
						}

						reconnectAttempts = 0;
						hasScheduledRetry = false;
						isHandlingError = false;
						dispatch(setAMWebSocketConnected(true));
						dispatch(setAMConnectionError(false));
						dispatch(setAMConnectionLoading(false));
						dispatch(setNextRetryTime(null));
						if (window.retryTimeout) {
							clearTimeout(window.retryTimeout);
							window.retryTimeout = null;
						}
						if (!pollingInterval) {
							dispatch(pollAirmeshChannels());
						}
					},

					onmessage: event => {
						try {
							const data = JSON.parse(event.data);
							bufferAndDispatch(dispatch, data.data);
						} catch (error) {
							console.error('[SSE] Failed to parse message:', error);
						}
					},

					onclose: () => {
						if (isIntentionalAbort || abortController?.signal.aborted) {
							//[SSE] Connection closed intentionally
							return;
						} else {
							// Server closed the connection - we should retry
							dispatch(setAMWebSocketConnected(false));
							dispatch(setAMConnectionLoading(false));
							retryConnection(dispatch, false);
						}
					},

					onerror: err => {
						console.error('[SSE] onerror', err);
						if (abortController) {
							abortController.abort(); // Stop any browser retries
						}
						if (!isIntentionalAbort && !isHandlingError) {
							isHandlingError = true;
							dispatch(setAMWebSocketConnected(false));
							dispatch(setAMConnectionLoading(false));
							retryConnection(dispatch, false);
						}
						// 👇 This is key! Stop the fetchEventSource loop.
						throw new Error('[SSE] onerror fatal — terminating fetchEventSource');
					}
				});
			} catch (error) {
				if (abortController) {
					abortController.abort();
				}
				// Handle initial connection failure
				if (!isIntentionalAbort && !isHandlingError) {
					isHandlingError = true;
					dispatch(setAMWebSocketConnected(false));
					dispatch(setAMConnectionError(true));
					dispatch(setAMConnectionLoading(false));
					retryConnection(dispatch, false);
				}
			}
		} catch (error) {
			// Handle token fetch failure
			if (!isIntentionalAbort && !isHandlingError) {
				isHandlingError = true;
				dispatch(setAMWebSocketConnected(false));
				dispatch(setAMConnectionError(true));
				dispatch(setAMConnectionLoading(false));
				retryConnection(dispatch, false);
			}
		}
	};

export const stopAirmeshChannelPolling = () => {
	if (pollingInterval) {
		clearInterval(pollingInterval);
		pollingInterval = null;
	}
};

export const unsubscribeFromSSE = () => async (dispatch, getState) => {
	// If we're in the middle of a retry, let it complete
	if (isRetrying) {
		return;
	}
	// Prevent any buffered dispatch from re-populating traffic after disconnect
	clearSseBuffer();
	isIntentionalAbort = true;
	reconnectAttempts = 0;
	hasScheduledRetry = false;
	isHandlingError = false;
	isFirstChannelPoll = true; // Reset the flag when unsubscribing
	dispatch(setReconnectAttempts(0));
	dispatch(setNextRetryTime(null));
	if (abortController) {
		abortController.abort();
		abortController = null;
	}
	stopAirmeshChannelPolling();
	if (tokenRefreshTimeout) {
		clearTimeout(tokenRefreshTimeout);
		tokenRefreshTimeout = null;
	}
	if (window.retryTimeout) {
		clearTimeout(window.retryTimeout);
		window.retryTimeout = null;
	}
	dispatch(setAMWebSocketConnected(false));
	dispatch(setAMConnectionError(false));
	dispatch(setAMConnectionLoading(false));
	dispatch(resetAMState());
};
